import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Button,
  Table,
} from 'reactstrap';
import OfficeBuildingIcon from 'mdi-react/OfficeBuildingIcon';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import ChartLineVariantIcon from 'mdi-react/ChartLineVariantIcon';
import GavelIcon from 'mdi-react/GavelIcon';
import TagIcon from 'mdi-react/TagIcon';
import StarIcon from 'mdi-react/StarIcon';
import CalendarIcon from 'mdi-react/CalendarIcon';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import ClipboardTextIcon from 'mdi-react/ClipboardTextIcon';
import BellRingIcon from 'mdi-react/BellRingIcon';
import ImageMultipleIcon from 'mdi-react/ImageMultipleIcon';
import ClubheartIcon from '../../shared/components/custom/ClubheartIcon';
import CreditCardMultipleIcon from 'mdi-react/CreditCardMultipleIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import AccountIcon from 'mdi-react/AccountIcon';
import TrophyOutlineIcon from 'mdi-react/TrophyOutlineIcon';
import StarCircleOutlineIcon from 'mdi-react/StarCircleOutlineIcon';
import AccountStarIcon from 'mdi-react/AccountStarIcon';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import { formatCurrencyNumber } from '../../util/functions';
import useConfig from '../../util/useConfig';
import DisplayCounterSkeleton from '../_uiComponents/DisplayCounterSkeleton';
import FormCard from '../../shared/components/custom/FormCard';
import DisplayCounter from '../_uiComponents/DisplayCounter';
import {
  getQuarterRange,
  isWithinRange,
  overlapsRange,
  formatQoqPill,
  qoqTone,
} from '../../util/quarterRange';

const p = partial('QuarterlyReview');
const s = partial('shared');
const offersPage = partial('OffersPage');
const sidebar = partial('SidebarContent');
const tx = partial('transactions');
const mc = partial('matchCenter');

// None of these list endpoints support server-side date filtering (except
// transactions and deals), so we page through the most recent records and
// filter by date client-side. 500 comfortably covers a single quarter for
// the data volumes this admin tool deals with.
const PAGE_SIZE = 500;

// runFetchers already tolerates individual fetcher failures via
// Promise.allSettled, but that only helps once a request actually rejects.
// A request that never settles (a hung/slow backend call) would otherwise
// leave its group's loading state stuck forever, since nothing downstream
// ever fires. This forces a rejection after FETCH_TIMEOUT_MS so that case
// is treated the same as any other failure.
const FETCH_TIMEOUT_MS = 15000;
const withTimeout = (promise, ms = FETCH_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_resolve, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), ms);
    }),
  ]);

const percentOf = (count, total) =>
  total ? Math.round((count / total) * 100) : 0;

// The group data behind this page is expensive to fetch (a dozen+ list
// endpoints per quarter), so once fetched it's cached in localStorage per
// quarter and reused for a year rather than re-fetched on every visit.
// Bump CACHE_VERSION whenever a fetcher's returned shape changes (new/renamed
// field) - otherwise a cached entry from before the change keeps being read
// and silently serves stats missing the new field until it expires or is
// manually cleared.
const CACHE_VERSION = 2;
const CACHE_PREFIX = `quarterlyReviewCache-v${CACHE_VERSION}-`;
const CACHE_TTL_MS = 365 * 24 * 60 * 60 * 1000;

// useConfig can switch clubs at runtime without a reload (path-based club
// switching - see src/util/useConfig.jsx), so this page can be viewed for
// more than one club within the same browser origin/localStorage. The club
// id has to be part of the key, or club B's cache would be read while
// viewing club A (and vice versa).
const cacheKey = (clubId, quartersAgo) =>
  `${CACHE_PREFIX}${clubId}-${quartersAgo}`;

const readCache = (clubId, quartersAgo) => {
  try {
    const raw = localStorage.getItem(cacheKey(clubId, quartersAgo));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      return null;
    }
    return parsed.groupState || null;
  } catch {
    return null;
  }
};

const writeCache = (clubId, quartersAgo, state) => {
  try {
    localStorage.setItem(
      cacheKey(clubId, quartersAgo),
      JSON.stringify({ timestamp: Date.now(), groupState: state })
    );
  } catch {
    // Cache is a convenience only (e.g. localStorage full/disabled) - fine
    // to silently skip persisting it.
  }
};

const clearCache = (clubId) => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(`${CACHE_PREFIX}${clubId}-`))
    .forEach((key) => localStorage.removeItem(key));
};

// How many quarters back a viewer can browse to.
const QUARTER_OPTIONS = [1, 2, 3, 4, 5, 6];

// cardCount drives the skeleton placeholder shown while a group is loading.
const GROUPS = [
  { key: 'partners', title: p('groupPartners'), cardCount: 3 },
  { key: 'earnings', title: p('groupEarnings'), cardCount: 3 },
  { key: 'content', title: p('groupContent'), cardCount: 6 },
  { key: 'finance', title: p('groupFinance'), cardCount: 3 },
  { key: 'sponsors', title: p('groupSponsors'), cardCount: 3 },
];

// Runs every fetcher in `fetchers` (an object of key -> async fn) for a
// given quarter range, in parallel, tolerating individual failures.
const runFetchers = async (fetchers, forRange) => {
  const keys = Object.keys(fetchers);
  const results = await Promise.allSettled(
    keys.map((key) => fetchers[key](forRange))
  );
  return keys.reduce((acc, key, index) => {
    acc[key] =
      results[index].status === 'fulfilled' ? results[index].value : {};
    return acc;
  }, {});
};

const buildGroupFetchers = (config) => {
  const fetchCompanies = async ({ start, end }) => {
    const companies = await Api.companies.getClubCompanies();
    const active = (companies || []).filter(
      (item) => item?.isAppVisible && item?.isSponsor
    );
    const inRange = active.filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    return { count: inRange.length, total: active.length };
  };

  // Employees and tiers carry no creation/assignment date anywhere in the
  // API, so these are a current snapshot rather than a quarter count.
  const fetchEmployees = async () => {
    const { data } = await Api.companies.getAllCompaniesEmployees();
    return {
      total: data.length,
      connected: data.filter((emp) => emp.userId).length,
    };
  };

  const fetchTiers = async () => {
    const { data } = await Api.tiers.allTiers({ withCount: true });
    const excluded = ['Default tier', 'Guest user'];
    const relevant = (data || []).filter(
      (tier) => !excluded.includes(tier.name)
    );
    return {
      count: relevant.reduce((sum, tier) => sum + (tier.userCount || 0), 0),
    };
  };

  const fetchDeals = async ({ startISO, endISO }) => {
    const data = await Api.companies.getDealStatistics({
      startDate: startISO,
      endDate: endISO,
    });
    return {
      count: data?.dealsCount || 0,
      turnover: data?.turnover || 0,
    };
  };

  const fetchAuctions = async ({ start, end }) => {
    const { data } = await Api.auctions.getAuctionHouses(
      { page: 1, take: PAGE_SIZE },
      { orderBy: 'createdAt', direction: 'DESC' }
    );
    const inRange = (data || []).filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    return {
      count: inRange.length,
      totalValue: inRange.reduce((sum, item) => sum + (item.value || 0), 0),
    };
  };

  const fetchOffers = async ({ start, end }) => {
    const [active, expired] = await Promise.all([
      Api.offers.fetchAllOffers(config.id, 1, PAGE_SIZE, 'active'),
      Api.offers.fetchAllOffers(config.id, 1, PAGE_SIZE, 'expired'),
    ]);
    const combined = [
      ...(active?.data?.offers || []),
      ...(expired?.data?.offers || []),
    ];
    const createdInRange = combined.filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    const runningInRange = combined.filter((item) =>
      overlapsRange(item.startDate, item.endDate, start, end)
    );
    return { count: createdInRange.length, running: runningInRange.length };
  };

  const fetchGoalClub = async ({ start, end }) => {
    const { data } =
      await Api.goalSponsor.subscriptionPlans.getSubscriptionPlans(
        { page: 1, take: PAGE_SIZE },
        { orderBy: 'createdAt', direction: 'DESC' }
      );
    const inRange = (data || []).filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    return {
      count: inRange.length,
      totalValue: inRange.reduce(
        (sum, item) => sum + (item.paymentMetadata?.sum || 0),
        0
      ),
    };
  };

  const fetchEvents = async ({ start, end }) => {
    const { data } = await Api.events.all();
    const inRange = (data || []).filter((item) =>
      isWithinRange(item.startDate, start, end)
    );
    return {
      count: inRange.length,
      participants: inRange.reduce(
        (sum, item) => sum + (item.participants?.length || 0),
        0
      ),
    };
  };

  const fetchNews = async ({ start, end }) => {
    const { data } = await Api.news.getNews(
      { page: 1, take: PAGE_SIZE },
      { orderBy: 'publicationDate', direction: 'DESC' }
    );
    const inRange = (data || []).filter((item) =>
      isWithinRange(item.publicationDate, start, end)
    );
    return { count: inRange.length };
  };

  const fetchQuestionaires = async ({ start, end }) => {
    const { data } = await Api.questionaire.getQuestionaires(
      { page: 1, take: PAGE_SIZE },
      { orderBy: 'createdAt', direction: 'DESC' }
    );
    const inRange = (data || []).filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    return {
      count: inRange.length,
      answers: inRange.reduce((sum, item) => sum + (item.answersCount || 0), 0),
    };
  };

  // sms/emails are confirmed fields on this response; push is not shown
  // anywhere it's used today but may still be returned by the API.
  const fetchMessages = async ({ startISO, endISO }) => {
    const data = await Api.transactions.getMessagesStatistics({
      startDate: startISO,
      endDate: endISO,
    });
    return { count: data?.push || 0 };
  };

  // No club-wide "all tiles" endpoint exists - only per-group, so we
  // fetch every spotlight group's tiles and flatten them.
  const fetchSpotlightTiles = async ({ start, end }) => {
    const { data: groups } = await Api.spotlight.getGroups(
      { page: 1, take: PAGE_SIZE },
      { orderBy: 'createdAt', direction: 'DESC' }
    );
    const tileLists = await Promise.all(
      (groups || []).map((group) =>
        Api.spotlight.getTilesForGroup(group.id).catch(() => [])
      )
    );
    const allTiles = tileLists.flat();
    const inRange = allTiles.filter((tile) =>
      isWithinRange(tile.publicationDate, start, end)
    );
    return { count: inRange.length };
  };

  const fetchTransactions = async ({ startISO, endISO }) => {
    const data = await Api.transactions.getTransactionsStatistics({
      startDate: startISO,
      endDate: endISO,
    });
    return {
      count: data?.total?.transactions || 0,
      turnover: data?.total?.turnover || 0,
    };
  };

  const fetchUsers = async ({ start, end }) => {
    const { users, total } = await Api.users.getTeamUsers(1, PAGE_SIZE, {
      createdAt: 'desc',
    });
    const inRange = (users || []).filter((item) =>
      isWithinRange(item.createdAt, start, end)
    );
    return { count: inRange.length, total: total || 0 };
  };

  // match sponsor and PR (player-rating) sponsor both live on the same
  // match_center match object, so one fetch covers both stats. Concluded
  // and upcoming matches are separate pages on this endpoint.
  const fetchMatchSponsors = async ({ start, end }) => {
    const teamIds = (config.teams || []).map((team) => team.id);
    const [upcoming, concluded] = await Promise.all([
      withTimeout(
        Api.matchCenter.getUpcomingMatches(1, PAGE_SIZE, teamIds, false)
      ),
      withTimeout(
        Api.matchCenter.getUpcomingMatches(1, PAGE_SIZE, teamIds, true)
      ),
    ]);
    const allMatches = [
      ...(upcoming?.data?.matches || []),
      ...(concluded?.data?.matches || []),
    ];
    const inRange = allMatches.filter((match) =>
      isWithinRange(match.matchStart, start, end)
    );
    const matchSponsorCount = inRange.filter(
      (match) => match.settings?.settings?.sponsor?.id
    ).length;
    const prSponsorCount = inRange.filter(
      (match) => match.playerRatingCompany?.companyId
    ).length;
    return {
      total: inRange.length,
      matchSponsorCount,
      matchSponsorEmpty: inRange.length - matchSponsorCount,
      prSponsorCount,
      prSponsorEmpty: inRange.length - prSponsorCount,
    };
  };

  // No club-wide player list exists - players are scoped per team, so
  // this loops every team and flattens. No creation date is exposed for
  // players anywhere in the API, so like employees/tiers this is a
  // current snapshot rather than a quarter count.
  const fetchPlayerSponsors = async () => {
    const teamPlayers = await Promise.all(
      (config.teams || []).map((team) =>
        withTimeout(Api.players.getPLayersFromNtb(team.id))
          .then((res) => res.data || [])
          .catch(() => [])
      )
    );
    const activePlayers = teamPlayers.flat().filter((player) => player.active);
    const withSponsor = activePlayers.filter((player) => player.companyId);
    return { count: withSponsor.length, total: activePlayers.length };
  };

  return {
    partners: {
      companies: fetchCompanies,
      employees: fetchEmployees,
      deals: fetchDeals,
    },
    earnings: {
      auctions: fetchAuctions,
      goalClub: fetchGoalClub,
      tiers: fetchTiers,
    },
    content: {
      offers: fetchOffers,
      events: fetchEvents,
      news: fetchNews,
      questionaires: fetchQuestionaires,
      messages: fetchMessages,
      spotlightTiles: fetchSpotlightTiles,
    },
    finance: {
      transactions: fetchTransactions,
      users: fetchUsers,
    },
    sponsors: {
      matches: fetchMatchSponsors,
      players: fetchPlayerSponsors,
    },
  };
};

// All groups' stats for one quarter, without the per-group progressive
// rendering the selected quarter gets - used to fill in the trend.
const fetchQuarterStats = async (groupFetchers, quartersAgo) => {
  const forRange = getQuarterRange(quartersAgo);
  const entries = await Promise.all(
    Object.keys(groupFetchers).map(async (key) => [
      key,
      await runFetchers(groupFetchers[key], forRange),
    ])
  );
  return Object.fromEntries(entries);
};

// A cached quarter holds both its own stats and the previous quarter's, so
// one cache entry fills two points of the trend.
const pickStats = (state, field) =>
  GROUPS.reduce((acc, group) => {
    acc[group.key] = state?.[group.key]?.[field] || {};
    return acc;
  }, {});

// Only quarter-scoped numbers belong in a trend - snapshot values (employees,
// tiers, total users, player sponsors) would just repeat today's number for
// every past quarter.
const TREND_METRICS = [
  {
    key: 'companies',
    label: () => s('companies'),
    get: (st) => st.partners?.companies?.count,
  },
  {
    key: 'deals',
    label: () => s('deals'),
    get: (st) => st.partners?.deals?.turnover,
    money: true,
  },
  {
    key: 'auctions',
    label: () => s('auctions'),
    get: (st) => st.earnings?.auctions?.totalValue,
    money: true,
  },
  {
    key: 'goalClub',
    label: () => tx('goalSponsor'),
    get: (st) => st.earnings?.goalClub?.totalValue,
    money: true,
  },
  {
    key: 'transactions',
    label: () => s('transactions'),
    get: (st) => st.finance?.transactions?.turnover,
    money: true,
  },
  {
    key: 'newUsers',
    label: () => s('newUsers'),
    get: (st) => st.finance?.users?.count,
  },
  {
    key: 'offers',
    label: () => offersPage('offers'),
    get: (st) => st.content?.offers?.count,
  },
  {
    key: 'events',
    label: () => s('events'),
    get: (st) => st.content?.events?.count,
  },
  {
    key: 'news',
    label: () => s('news'),
    get: (st) => st.content?.news?.count,
  },
  {
    key: 'questionaires',
    label: () => sidebar('questionaire'),
    get: (st) => st.content?.questionaires?.count,
  },
  {
    key: 'messages',
    label: () => p('pushMessages'),
    get: (st) => st.content?.messages?.count,
  },
  {
    key: 'spotlightTiles',
    label: () => p('spotlightTiles'),
    get: (st) => st.content?.spotlightTiles?.count,
  },
  {
    key: 'matchSponsors',
    label: () => mc('matchSponsor'),
    get: (st) => st.sponsors?.matches?.matchSponsorCount,
  },
  {
    key: 'prSponsors',
    label: () => mc('pRSponsor'),
    get: (st) => st.sponsors?.matches?.prSponsorCount,
  },
];

const Sparkline = ({ values }) => {
  const width = 84;
  const height = 24;
  const pad = 3;
  const defined = values.filter((value) => value !== undefined);
  if (defined.length < 2) {
    return null;
  }
  const min = Math.min(...defined);
  const max = Math.max(...defined);
  const x = (index) => pad + (index / (values.length - 1)) * (width - pad * 2);
  const y = (value) =>
    max === min
      ? height / 2
      : pad + (1 - (value - min) / (max - min)) * (height - pad * 2);

  let path = '';
  let penDown = false;
  values.forEach((value, index) => {
    if (value === undefined) {
      penDown = false;
      return;
    }
    path += `${penDown ? 'L' : 'M'}${x(index)},${y(value)} `;
    penDown = true;
  });
  const lastIndex =
    values.length - 1 - [...values].reverse().findIndex((v) => v !== undefined);

  return (
    <svg width={width} height={height} aria-hidden="true">
      <path
        d={path}
        fill="none"
        stroke="#0469dc"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={x(lastIndex)}
        cy={y(values[lastIndex])}
        r={2.5}
        fill="#0469dc"
      />
    </svg>
  );
};

const scopeLabelStyle = {
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  fontWeight: 600,
  fontSize: 11,
};

const QuarterlyReviewPage = () => {
  const config = useConfig();
  const clubId = config.basename || 'default';
  const [quartersAgo, setQuartersAgo] = useState(1);
  const range = getQuarterRange(quartersAgo);
  const prevRange = getQuarterRange(quartersAgo + 1);
  const [groupState, setGroupState] = useState({});
  const [forceReload, setForceReload] = useState(0);
  const [trendStats, setTrendStats] = useState({});
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    const cached = readCache(clubId, quartersAgo);
    if (cached) {
      setGroupState(cached);
      return;
    }

    const groupFetchers = buildGroupFetchers(config);
    // Built here rather than reusing range/prevRange, which are new objects
    // on every render and would re-run this effect each time.
    const forRange = getQuarterRange(quartersAgo);
    const forPrevRange = getQuarterRange(quartersAgo + 1);

    // Each group loads and renders independently - a slow domain in one
    // group never blocks the others from showing up. Once every group has
    // settled, the combined result is cached for this quarter.
    const groupKeys = Object.keys(groupFetchers);
    const groupPromises = groupKeys.map((key) => {
      setGroupState((prevState) => ({
        ...prevState,
        [key]: { loading: true, stats: {}, prevStats: {} },
      }));
      return Promise.all([
        runFetchers(groupFetchers[key], forRange),
        runFetchers(groupFetchers[key], forPrevRange),
      ]).then(([stats, prevStats]) => {
        const groupResult = { loading: false, stats, prevStats };
        setGroupState((prevState) => ({
          ...prevState,
          [key]: groupResult,
        }));
        return groupResult;
      });
    });

    Promise.all(groupPromises).then((groupResults) => {
      const finalState = groupKeys.reduce((acc, key, index) => {
        acc[key] = groupResults[index];
        return acc;
      }, {});
      writeCache(clubId, quartersAgo, finalState);
    });
  }, [config, clubId, quartersAgo, forceReload]);

  const selectedReady = GROUPS.every(
    (group) => groupState[group.key] && !groupState[group.key].loading
  );

  // The trend only starts once the selected quarter has finished, so the
  // two never fetch the same quarter at once. Quarters are then filled in
  // one at a time (to keep the load on the backend down) and cached, so this
  // only costs anything the first time a quarter is seen.
  useEffect(() => {
    setTrendLoading(false);
    if (!selectedReady) {
      return undefined;
    }
    let cancelled = false;
    const lastQuarter = QUARTER_OPTIONS[QUARTER_OPTIONS.length - 1];

    const known = {};
    for (let n = 1; n <= lastQuarter + 1; n += 1) {
      const own = readCache(clubId, n);
      const newer = n > 1 ? readCache(clubId, n - 1) : null;
      if (own) known[n] = pickStats(own, 'stats');
      else if (newer) known[n] = pickStats(newer, 'prevStats');
    }
    known[quartersAgo] = pickStats(groupState, 'stats');
    known[quartersAgo + 1] = pickStats(groupState, 'prevStats');
    setTrendStats({ ...known });

    const persist = () => {
      QUARTER_OPTIONS.forEach((n) => {
        if (!readCache(clubId, n) && known[n] && known[n + 1]) {
          writeCache(
            clubId,
            n,
            GROUPS.reduce((acc, group) => {
              acc[group.key] = {
                loading: false,
                stats: known[n][group.key],
                prevStats: known[n + 1][group.key],
              };
              return acc;
            }, {})
          );
        }
      });
    };

    // The quarter after the last option is only needed to complete the
    // last option's own cache entry.
    const toFetch = [];
    for (let n = 1; n <= lastQuarter + 1; n += 1) {
      const needed = n <= lastQuarter || !readCache(clubId, lastQuarter);
      if (!known[n] && needed) toFetch.push(n);
    }
    if (!toFetch.length) {
      return undefined;
    }

    const groupFetchers = buildGroupFetchers(config);
    setTrendLoading(true);
    (async () => {
      for (let i = 0; i < toFetch.length; i += 1) {
        const n = toFetch[i];
        const stats = await fetchQuarterStats(groupFetchers, n);
        if (cancelled) return;
        known[n] = stats;
        setTrendStats((prev) => ({ ...prev, [n]: stats }));
        persist();
      }
      if (!cancelled) setTrendLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // groupState is read once the selected quarter is ready; depending on it
    // would restart the trend on every per-group update.
    // eslint-disable-next-line
  }, [clubId, forceReload, selectedReady]);

  const handleClearCache = () => {
    clearCache(clubId);
    setTrendStats({});
    setForceReload((n) => n + 1);
  };

  const rangeLabel = `${range.start.format('MMM D')} – ${range.end.format('MMM D, YYYY')}`;

  const money = (cents) =>
    `${formatCurrencyNumber((cents || 0) / 100)} ${config.currencyCaps}`;

  const prevQuarterLabel = `Q${prevRange.quarter} ${prevRange.year}`;

  // The hover text names the quarter and the value the pill is measured
  // against, since the pill alone doesn't say what it compares to.
  const growth = (
    current,
    previous,
    format = (value) => Number(value).toLocaleString()
  ) => ({
    pill: formatQoqPill(current, previous, s('new')),
    pillTone: qoqTone(current, previous),
    pillTitle: p('comparedWith', {
      quarter: prevQuarterLabel,
      value: format(previous ?? 0),
    }),
  });

  // scope: 'now' marks a current snapshot (nothing dated to compare), the
  // rest are counts for the selected quarter.
  const cardsForGroup = (key, stats, prevStats) => {
    switch (key) {
      case 'partners':
        return [
          {
            id: 'qr-companies',
            title: s('companies'),
            value: stats.companies?.count ?? 0,
            text: p('outOfTotalCaption', {
              total: stats.companies?.total ?? 0,
            }),
            icon: <OfficeBuildingIcon style={{ width: 14 }} />,
            ...growth(stats.companies?.count ?? 0, prevStats.companies?.count),
          },
          {
            id: 'qr-deals',
            title: s('deals'),
            value: money(stats.deals?.turnover),
            text: p('dealsCaption', { count: stats.deals?.count ?? 0 }),
            icon: <ChartLineVariantIcon style={{ width: 14 }} />,
            ...growth(
              stats.deals?.turnover ?? 0,
              prevStats.deals?.turnover,
              money
            ),
          },
          {
            id: 'qr-employees',
            scope: 'now',
            title: s('employees'),
            value: stats.employees?.total ?? 0,
            text: p('connectedCaption', {
              count: stats.employees?.connected ?? 0,
            }),
            icon: <AccountGroupIcon style={{ width: 14 }} />,
          },
        ];
      case 'earnings':
        return [
          {
            id: 'qr-auctions',
            title: s('auctions'),
            value: money(stats.auctions?.totalValue),
            text: p('auctionsCaption', { count: stats.auctions?.count ?? 0 }),
            icon: <GavelIcon style={{ width: 14 }} />,
            ...growth(
              stats.auctions?.totalValue ?? 0,
              prevStats.auctions?.totalValue,
              money
            ),
          },
          {
            id: 'qr-goal-club',
            title: tx('goalSponsor'),
            value: money(stats.goalClub?.totalValue),
            text: p('goalClubCaption', { count: stats.goalClub?.count ?? 0 }),
            icon: <ClubheartIcon style={{ width: 14 }} />,
            ...growth(
              stats.goalClub?.totalValue ?? 0,
              prevStats.goalClub?.totalValue,
              money
            ),
          },
          {
            id: 'qr-tiers',
            scope: 'now',
            title: p('tiers'),
            value: stats.tiers?.count ?? 0,
            text: p('activeMembersCaption'),
            icon: <StarIcon style={{ width: 14 }} />,
          },
        ];
      case 'content':
        return [
          {
            id: 'qr-offers',
            title: offersPage('offers'),
            value: stats.offers?.count ?? 0,
            text: p('runningCaption', { count: stats.offers?.running ?? 0 }),
            icon: <TagIcon style={{ width: 14 }} />,
            ...growth(stats.offers?.count ?? 0, prevStats.offers?.count),
          },
          {
            id: 'qr-events',
            title: s('events'),
            value: stats.events?.count ?? 0,
            text: `${stats.events?.participants ?? 0} ${s('participants')}`,
            icon: <CalendarIcon style={{ width: 14 }} />,
            ...growth(stats.events?.count ?? 0, prevStats.events?.count),
          },
          {
            id: 'qr-news',
            title: s('news'),
            value: stats.news?.count ?? 0,
            icon: <NewspaperIcon style={{ width: 14 }} />,
            ...growth(stats.news?.count ?? 0, prevStats.news?.count),
          },
          {
            id: 'qr-questionaires',
            title: sidebar('questionaire'),
            value: stats.questionaires?.count ?? 0,
            text: p('answersCaption', {
              count: stats.questionaires?.answers ?? 0,
            }),
            icon: <ClipboardTextIcon style={{ width: 14 }} />,
            ...growth(
              stats.questionaires?.count ?? 0,
              prevStats.questionaires?.count
            ),
          },
          {
            id: 'qr-messages',
            title: p('pushMessages'),
            value: stats.messages?.count ?? 0,
            icon: <BellRingIcon style={{ width: 14 }} />,
            ...growth(stats.messages?.count ?? 0, prevStats.messages?.count),
          },
          {
            id: 'qr-spotlight-tiles',
            title: p('spotlightTiles'),
            value: stats.spotlightTiles?.count ?? 0,
            icon: <ImageMultipleIcon style={{ width: 14 }} />,
            ...growth(
              stats.spotlightTiles?.count ?? 0,
              prevStats.spotlightTiles?.count
            ),
          },
        ];
      case 'finance':
        return [
          {
            id: 'qr-transactions',
            title: s('transactions'),
            value: money(stats.transactions?.turnover),
            text: p('transactionsCaption', {
              count: stats.transactions?.count ?? 0,
            }),
            icon: <CreditCardMultipleIcon style={{ width: 14 }} />,
            ...growth(
              stats.transactions?.turnover ?? 0,
              prevStats.transactions?.turnover,
              money
            ),
          },
          {
            id: 'qr-new-users',
            title: s('newUsers'),
            value: stats.users?.count ?? 0,
            icon: <AccountMultipleIcon style={{ width: 14 }} />,
            ...growth(stats.users?.count ?? 0, prevStats.users?.count),
          },
          {
            // No pill - there's no real previous-quarter total to compare
            // against (only this quarter's new-user count), so a QoQ pill
            // derived from that would imply zero churn and overstate growth.
            id: 'qr-total-users',
            scope: 'now',
            title: s('totalUsers'),
            value: stats.users?.total ?? 0,
            icon: <AccountIcon style={{ width: 14 }} />,
          },
        ];
      case 'sponsors': {
        const matchTotal = stats.matches?.total ?? 0;
        const matchSponsors = stats.matches?.matchSponsorCount ?? 0;
        const prSponsors = stats.matches?.prSponsorCount ?? 0;
        const playerTotal = stats.players?.total ?? 0;
        const playerSponsors = stats.players?.count ?? 0;
        return [
          {
            id: 'qr-match-sponsors',
            title: mc('matchSponsor'),
            value: matchSponsors,
            text: p('matchCoverageCaption', {
              percent: percentOf(matchSponsors, matchTotal),
              total: matchTotal,
            }),
            progress: percentOf(matchSponsors, matchTotal),
            icon: <TrophyOutlineIcon style={{ width: 14 }} />,
            ...growth(matchSponsors, prevStats.matches?.matchSponsorCount),
          },
          {
            id: 'qr-pr-sponsors',
            title: mc('pRSponsor'),
            value: prSponsors,
            text: p('matchCoverageCaption', {
              percent: percentOf(prSponsors, matchTotal),
              total: matchTotal,
            }),
            progress: percentOf(prSponsors, matchTotal),
            icon: <StarCircleOutlineIcon style={{ width: 14 }} />,
            ...growth(prSponsors, prevStats.matches?.prSponsorCount),
          },
          {
            id: 'qr-player-sponsors',
            scope: 'now',
            title: p('playersWithSponsor'),
            value: playerSponsors,
            text: p('playerCoverageCaption', {
              percent: percentOf(playerSponsors, playerTotal),
              total: playerTotal,
            }),
            progress: percentOf(playerSponsors, playerTotal),
            icon: <AccountStarIcon style={{ width: 14 }} />,
          },
        ];
      }
      default:
        return [];
    }
  };

  const renderCard = (card) => (
    <DisplayCounter
      key={card.id}
      id={card.id}
      title={card.title}
      value={`${card.value}`}
      text={card.text}
      icon={card.icon}
      pill={card.pill}
      pillTone={card.pillTone}
      pillTitle={card.pillTitle}
      progress={card.progress}
    />
  );

  // Oldest first, so the trend reads left to right.
  const trendQuarters = [...QUARTER_OPTIONS].reverse();

  const formatMetric = (metric, value) => {
    if (value === undefined) return '–';
    return metric.money
      ? formatCurrencyNumber(value / 100)
      : Number(value).toLocaleString();
  };

  return (
    <Container>
      <Row>
        <Col>
          <h3 className="page-title">{p('title')}</h3>
          <h4 className="subhead mb-3">
            {p('subtitle', {
              quarter: range.quarter,
              year: range.year,
              range: rangeLabel,
            })}
          </h4>
        </Col>
      </Row>
      <Nav pills className="mb-3">
        {QUARTER_OPTIONS.map((n) => {
          const option = getQuarterRange(n);
          return (
            <NavItem key={n}>
              <NavLink
                role="button"
                active={quartersAgo === n}
                onClick={() => setQuartersAgo(n)}
              >
                {`Q${option.quarter} ${option.year}`}
              </NavLink>
            </NavItem>
          );
        })}
      </Nav>
      {GROUPS.map((group) => {
        const state = groupState[group.key] || {
          loading: true,
          stats: {},
          prevStats: {},
        };
        const cards = state.loading
          ? []
          : cardsForGroup(group.key, state.stats, state.prevStats);
        const quarterCards = cards.filter((card) => card.scope !== 'now');
        const nowCards = cards.filter((card) => card.scope === 'now');
        return (
          <div className="mb-3" key={group.key}>
            <FormCard title={group.title}>
              {state.loading ? (
                <DisplayCounterSkeleton count={group.cardCount} />
              ) : (
                <>
                  {quarterCards.length > 0 && (
                    <div>
                      <small
                        className="text-muted d-block mb-2"
                        style={scopeLabelStyle}
                      >
                        {p('thisQuarter')}
                      </small>
                      {quarterCards.map(renderCard)}
                    </div>
                  )}
                  {nowCards.length > 0 && (
                    <div className={quarterCards.length > 0 ? 'mt-2' : ''}>
                      <small
                        className="text-muted d-block mb-2"
                        style={scopeLabelStyle}
                      >
                        {p('rightNow')}
                      </small>
                      {nowCards.map(renderCard)}
                    </div>
                  )}
                </>
              )}
            </FormCard>
          </div>
        );
      })}
      <FormCard
        title={p('trendTitle')}
        actions={
          trendLoading && (
            <small className="text-muted">{p('trendLoading')}</small>
          )
        }
        bodyClassName="form-card__body--flush"
        className="mb-3"
      >
        <Table responsive striped size="sm" className="mb-0">
          <thead>
            <tr>
              <th>{p('metric')}</th>
              {trendQuarters.map((n) => {
                const option = getQuarterRange(n);
                return (
                  <th
                    key={n}
                    className="text-right"
                    style={n === quartersAgo ? { color: '#0469dc' } : undefined}
                  >
                    {`Q${option.quarter} ${option.year}`}
                  </th>
                );
              })}
              <th />
            </tr>
          </thead>
          <tbody>
            {TREND_METRICS.map((metric) => {
              const values = trendQuarters.map((n) =>
                trendStats[n] ? metric.get(trendStats[n]) : undefined
              );
              return (
                <tr key={metric.key}>
                  <td>
                    {metric.label()}
                    {metric.money && (
                      <small className="text-muted">{` (${config.currencyCaps})`}</small>
                    )}
                  </td>
                  {values.map((value, index) => (
                    <td
                      key={trendQuarters[index]}
                      className="text-right"
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight:
                          trendQuarters[index] === quartersAgo ? 600 : 400,
                      }}
                    >
                      {formatMetric(metric, value)}
                    </td>
                  ))}
                  <td>
                    <Sparkline values={values} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </FormCard>
      <Row className="mb-3">
        <Col>
          <Button size="sm" color="secondary" onClick={handleClearCache}>
            {p('clearStoredData')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default QuarterlyReviewPage;
